import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, InjectionToken, Optional, PLATFORM_ID, SkipSelf } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Observable, catchError, map, of as observableOf, of, share, tap, throwError } from 'rxjs';
import { trustedTypesPolicy } from '../utils/helpers';
import { SvgLoader } from './svg-loader';

export const SERVER_URL = new InjectionToken<string>('SERVER_URL');

@Injectable()
export class SvgIconRegistryService {
  private policy: any;
  private document: Document;
  private iconsByUrl = new Map<string, SVGElement>();
  private iconsLoadingByUrl = new Map<string, Observable<SVGElement>>();

  constructor(
    private loader: SvgLoader,
    @Inject(WINDOW) private window: Window,
    @Optional() @Inject(SERVER_URL) protected serverUrl: string | undefined,
    @Optional() @Inject(DOCUMENT) private _document: any,
  ) {
    this.document = this._document;
    this.policy = trustedTypesPolicy(this.window);
  }

  /** Add a SVG to the registry by passing a name and the SVG. */
  addSvg(name: string, data: string) {
    if (!this.iconsByUrl.has(name)) {
      const div = this.document.createElement('DIV');
      div.innerHTML = this.policy ? this.policy.createHTML(data) : data;
      const svg = div.querySelector('svg') as SVGElement;
      this.iconsByUrl.set(name, svg);
    }
  }

  /** Load a SVG to the registry from a URL. */
  loadSvg(url: string, name: string = url): Observable<SVGElement | undefined> | undefined {
    // not sure if there should be a possibility to use name for server usage
    // so overriding it for now if provided
    // maybe should separate functionality for url and name use-cases
    if (this.serverUrl && url.match(/^(http(s)?):/) === null) {
      url = this.serverUrl + url;
      name = url;
    }
    if (this.iconsByUrl.has(name)) {
      return observableOf(this.iconsByUrl.get(name));
    } else if (this.iconsLoadingByUrl.has(name)) {
      return this.iconsLoadingByUrl.get(name);
    }
    const o = this.loader.getSvg(url).pipe(
      catchError(err => throwError(() => new Error(JSON.stringify(err)))),
      map(svg => {
        const div = this.document.createElement('DIV');
        div.innerHTML = this.policy ? this.policy.createHTML(svg) : svg;
        return div.querySelector('svg') as SVGElement;
      }),
      tap(svg => this.iconsByUrl.set(name, svg)),
      share(),
    ) as Observable<SVGElement>;

    this.iconsLoadingByUrl.set(name, o);
    return o;
  }

  /** Get loaded SVG from registry by name. (also works by url because of blended map) */
  getSvgByName(name: string): Observable<SVGElement | undefined> | undefined {
    if (this.iconsByUrl.has(name)) {
      return observableOf(this.iconsByUrl.get(name));
    } else if (this.iconsLoadingByUrl.has(name)) {
      return this.iconsLoadingByUrl.get(name);
    }
    return throwError(() => of(`No svg with name '${name}' has been loaded`));
  }

  /** Remove a SVG from the registry by URL (or name). */
  unloadSvg(url: string) {
    if (this.iconsByUrl.has(url)) {
      this.iconsByUrl.delete(url);
    }
  }
}

export const SVG_ICON_REGISTRY_PROVIDER_FACTORY = (
  parentRegistry: SvgIconRegistryService,
  loader: SvgLoader,
  platformId: any,
  serverUrl?: string,
  document?: any,
) => parentRegistry || new SvgIconRegistryService(loader, platformId, serverUrl, document);

export const SVG_ICON_REGISTRY_PROVIDER = {
  provide: SvgIconRegistryService,
  deps: [
    [new Optional(), new SkipSelf(), SvgIconRegistryService],
    SvgLoader,
    [PLATFORM_ID as InjectionToken<any>],
    [new Optional(), SERVER_URL as InjectionToken<string>],
    [new Optional(), DOCUMENT as InjectionToken<any>],
  ],
  useFactory: SVG_ICON_REGISTRY_PROVIDER_FACTORY,
};
